import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer1 from "../components/Footer1";
import "./SignUpPage.css";
import axios from "axios";
import CelebrationPopup from "../components/CelebrationPopup"; // ✅ Import Custom Popup

const SignUpPage = () => {
  const [isLogin, setIsLogin] = useState(false);
  const [step, setStep] = useState("form"); // form | otp | success

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    country: "",
    password: "",
    confirmPassword: "",
    emailOtp: "",
    mobileOtp: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ✅ POPUP STATE
  const [popup, setPopup] = useState({ 
    show: false, 
    type: "success", 
    title: "", 
    msg: "", 
    action: null // Function to run when popup closes (e.g., navigate)
  });

  // ✅ Helper to show popup
  const showPopup = (type, title, msg, action = null) => {
    setPopup({ show: true, type, title, msg, action });
  };

  // ✅ Helper to close popup & execute action
  const closePopup = () => {
    setPopup((prev) => ({ ...prev, show: false }));
    if (popup.action) popup.action();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- LOGIN FLOW ---
    if (isLogin) {
      setLoading(true);
      try {
        const res = await axios.post("http://localhost:8080/api/auth/login", {
          email: formData.email,
          password: formData.password,
        });

        if (res.data.token) {
          localStorage.setItem("authToken", res.data.token);
          
          // ✅ REPLACED ALERT WITH POPUP
          showPopup(
            "success", 
            "Welcome Back!", 
            "Login successful. Redirecting you to the art gallery...", 
            () => navigate("/") // Action: Navigate after closing
          );
        } else {
          setError("Invalid login response from server.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Login failed.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- SIGNUP STEP 1: SEND OTP ---
    if (step === "form") {
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (!formData.country.trim()) {
        setError("Please enter your country.");
        return;
      }

      setError("");
      setLoading(true);

      try {
        await axios.post("http://localhost:8080/api/auth/send-otp", {
          target: formData.email,
          type: "EMAIL"
        });

        // ✅ REPLACED ALERT WITH POPUP
        setStep("otp"); // Change background step immediately
        showPopup(
            "success", 
            "OTP Sent!", 
            `We have sent a verification code to ${formData.email}. Please check your inbox.`
        );
        
      } catch (err) {
        setError(err.response?.data?.message || "Failed to send OTP.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- SIGNUP STEP 2: VERIFY OTP & CREATE USER ---
    if (step === "otp") {
      setLoading(true);
      setError("");

      try {
        // Verify Email OTP
        const verifyRes = await axios.post("http://localhost:8080/api/auth/verify-otp", {
          target: formData.email,
          type: "Email",
          code: formData.emailOtp
        });

        if (verifyRes.data !== "Verified") {
          throw new Error("Invalid OTP");
        }

        // Create User
        await axios.post("http://localhost:8080/api/auth/signup", {
          name: formData.fullName,
          email: formData.email,
          mobile: formData.mobile,
          country: formData.country, 
          password: formData.password,
        });

        // ✅ SUCCESS (Popup optional here, or let the UI handle it)
        showPopup("success", "Account Created!", "Welcome to the community. You can now log in.");
        setStep("success");

      } catch (err) {
        console.error(err);
        // Use inline error for failure, or popup if preferred
        setError(err.response?.data?.message || "OTP verification or Signup failed.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {/* ✅ ADD POPUP COMPONENT */}
      <CelebrationPopup 
        isOpen={popup.show} 
        onClose={closePopup} 
        type={popup.type} 
        title={popup.title} 
        message={popup.msg} 
      />

      <div className="signup-page-container">
        <div className="signup-image-pane">
          <div className="signup-image-overlay">
            <h2 className="hero-subtitle" style={{ color: "white" }}>
              “Step into the realm of modern art & timeless imagination”
            </h2>
            <p>Join our community to save your favorite pieces, get exclusive updates, and enjoy a faster checkout.</p>
          </div>
        </div>

        <div className="signup-form-pane">
          <div className="form-content">
            <h3>
              {isLogin ? "Welcome Back" : step === "otp" ? "Verify Your Account" : step === "success" ? "Account Created!" : "Create Your Account"}
            </h3>

            {step === "form" && (
              <p className="form-subtext">
                {isLogin ? (
                  <>Don’t have an account? <span className="form-link" onClick={() => setIsLogin(false)}>Sign Up</span></>
                ) : (
                  <>Already have an account? <span className="form-link" onClick={() => setIsLogin(true)}>Log In</span></>
                )}
              </p>
            )}

            {step === "form" && (
              <form onSubmit={handleSubmit} className="smooth-form">
                {!isLogin && (
                  <>
                    <div className="form-group">
                      <label>Full Name</label>
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Mobile Number</label>
                      <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input type="text" name="country" value={formData.country} onChange={handleChange} required placeholder="e.g. India" />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange} required />
                </div>

                {!isLogin && (
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
                  </div>
                )}

                {error && <p className="form-error">{error}</p>}

                {!isLogin && (
                  <div className="form-terms">
                    <input type="checkbox" required />
                    <label>I agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</label>
                  </div>
                )}

                <button type="submit" className="btn-signup" disabled={loading}>
                  {loading ? (isLogin ? "Logging in..." : "Sending OTP...") : (isLogin ? "Log In" : "Create Account")}
                </button>
              </form>
            )}

            {step === "otp" && (
              <form onSubmit={handleSubmit} className="smooth-form">
                <p className="otp-text">Enter the OTP sent to your email.</p>
                <div className="form-group">
                  <label>Email OTP</label>
                  <input type="text" name="emailOtp" maxLength="6" value={formData.emailOtp} onChange={handleChange} required />
                </div>
                {error && <p className="form-error">{error}</p>}
                <button type="submit" className="btn-signup" disabled={loading}>{loading ? "Verifying..." : "Verify & Signup"}</button>
                <p className="form-link" onClick={() => setStep("form")}>← Go Back</p>
              </form>
            )}

            {step === "success" && (
              <div className="success-message smooth-form">
                <h4>Your account has been created successfully!</h4>
                <button className="btn-signup" onClick={() => { setIsLogin(true); setStep("form"); }}>Go to Login</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer1 />
    </>
  );
};

export default SignUpPage;